import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Avatar,
  Zoom,
  Fade,
  Chip,
} from '@mui/material';
import { 
  CheckCircle, 
  Login, 
  Refresh,
  Security,
  Lightbulb
} from '@mui/icons-material';
import { LIFERAY_HOST } from '../service/utils';

const SuccessMessage = () => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Animation d'entrée
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 300);

    // Nettoyage des données de session après 5 secondes
    const cleanupTimer = setTimeout(() => {
      sessionStorage.removeItem('resetSessionId');
      sessionStorage.removeItem('resetEmail');
      sessionStorage.removeItem('resetToken');
      sessionStorage.removeItem('resetUserEmail');
      sessionStorage.removeItem('resetUserName');
    }, 5000);

    return () => {
      clearTimeout(timer);
      clearTimeout(cleanupTimer);
    };
  }, []);

  const handleLogin = () => {
    window.location.href = `${LIFERAY_HOST}/web/bicici`; // Ajustez selon votre configuration
  };

  const handleStartOver = () => {
    window.location.reload();
  };

  return (
    <Box sx={{ textAlign: 'center' }}>
      {/* Animation de succès */}
      <Zoom in={true} timeout={800}>
        <Avatar
          sx={{
            width: 120,
            height: 120,
            bgcolor: 'success.main',
            margin: '0 auto 24px auto',
            boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)',
          }}
        >
          <CheckCircle sx={{ fontSize: 80 }} />
        </Avatar>
      </Zoom>

      <Fade in={showContent} timeout={1000}>
        <Box>
          {/* Titre principal */}
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            color="success.main"
            sx={{ fontWeight: 700, mb: 2 }}
          >
            🎉 Succès !
          </Typography>

          <Typography 
            variant="h5" 
            component="h3" 
            gutterBottom
            color="text.primary"
            sx={{ fontWeight: 600, mb: 3 }}
          >
            Mot de passe réinitialisé
          </Typography>

          {/* Message principal */}
          <Paper 
            sx={{ 
              p: 3, 
              mb: 4, 
              backgroundColor: 'success.50',
              border: '2px solid',
              borderColor: 'success.200'
            }}
          >
            <Typography variant="body1" color="text.primary" paragraph>
              Votre mot de passe a été modifié avec succès.
            </Typography>
            <Typography variant="body1" color="text.primary">
              Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </Typography>
          </Paper>

          {/* Conseil de sécurité */}
          <Paper 
            sx={{ 
              p: 3, 
              mb: 4, 
              backgroundColor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.200'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Lightbulb color="primary" />
              <Typography variant="h6" color="primary.main">
                Conseil de sécurité
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Gardez votre nouveau mot de passe en sécurité et ne le partagez avec personne.
              Nous vous recommandons d'utiliser un gestionnaire de mots de passe.
            </Typography>
          </Paper>

          {/* Informations de sécurité */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 4 }}>
            <Chip 
              icon={<Security />} 
              label="Connexion sécurisée" 
              color="success" 
              variant="outlined" 
            />
            <Chip 
              icon={<CheckCircle />} 
              label="Mot de passe fort" 
              color="primary" 
              variant="outlined" 
            />
          </Box>

          {/* Boutons d'action */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400, mx: 'auto' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleLogin}
              startIcon={<Login />}
              sx={{
                py: 1.5,
                background: '#339669',
                '&:hover': {
                  background: '#339669',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Se connecter maintenant
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={handleStartOver}
              startIcon={<Refresh />}
              sx={{ 
                py: 1.5,
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Nouvelle réinitialisation
            </Button>
          </Box>

          {/* Message d'information finale */}
          <Box sx={{ mt: 4, p: 2, backgroundColor: 'grey.100', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              🔒 Pour votre sécurité, cette session sera automatiquement nettoyée.
            </Typography>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

export default SuccessMessage;